/**
 * Modal Context for providing default props to input components
 * 
 * This context allows modals to automatically apply sensible defaults
 * to input components (TextInput, DropdownInput, etc.) without requiring
 * explicit props on each input.
 */

import React, { createContext, useContext, ReactNode } from 'react';

export interface ModalContextValue {
  /**
   * Default layer for input fields within modal
   * Layer 2 provides proper contrast against modal's layer-01 background
   */
  defaultInputLayer?: 1 | 2 | 3;
  
  /**
   * Default variant for dropdowns within modal
   * 'strong' variant provides better visual hierarchy in modal contexts
   */
  defaultDropdownVariant?: 'default' | 'strong' | 'subtle';
  
  /**
   * Whether we're inside a modal context
   */
  isInModal: boolean;
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

export interface ModalProviderProps {
  children: ReactNode;
  defaultInputLayer?: 1 | 2 | 3;
  defaultDropdownVariant?: 'default' | 'strong' | 'subtle';
}

/**
 * Provider component that wraps modal content to provide default input props
 */
export const ModalProvider: React.FC<ModalProviderProps> = ({
  children,
  defaultInputLayer = 2,
  defaultDropdownVariant = 'strong'
}) => {
  const value: ModalContextValue = {
    defaultInputLayer,
    defaultDropdownVariant,
    isInModal: true
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
};

/**
 * Hook to access modal context values
 * Returns undefined if not within a modal context
 */
export const useModalContext = () => {
  return useContext(ModalContext);
};

/**
 * Hook to get default input layer, with fallback
 * @param userLayer - User-provided layer prop (takes precedence)
 * @param fallbackLayer - Fallback if not in modal context (default: 1)
 */
export const useModalInputLayer = (
  userLayer?: 1 | 2 | 3,
  fallbackLayer: 1 | 2 | 3 = 1
): 1 | 2 | 3 => {
  const modalContext = useModalContext();
  
  // User prop takes precedence
  if (userLayer !== undefined) {
    return userLayer;
  }
  
  // Modal context default
  if (modalContext?.isInModal && modalContext.defaultInputLayer) {
    return modalContext.defaultInputLayer;
  }
  
  // Fallback
  return fallbackLayer;
};

/**
 * Hook to get default dropdown variant, with fallback
 * @param userVariant - User-provided variant prop (takes precedence)
 * @param fallbackVariant - Fallback if not in modal context (default: 'default')
 */
export const useModalDropdownVariant = (
  userVariant?: 'default' | 'strong' | 'subtle',
  fallbackVariant: 'default' | 'strong' | 'subtle' = 'default'
): 'default' | 'strong' | 'subtle' => {
  const modalContext = useModalContext();
  
  // User prop takes precedence
  if (userVariant !== undefined) {
    return userVariant;
  }
  
  // Modal context default
  if (modalContext?.isInModal && modalContext.defaultDropdownVariant) {
    return modalContext.defaultDropdownVariant;
  }
  
  // Fallback
  return fallbackVariant;
};
